/**
 * CareStart TTS voice picker dialog (Settings gear).
 * Uses SelectableChoiceCard for OpenAI, Gemini Live/Flash, or Deepgram Aura voices.
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import SelectableChoiceCard from 'components/common/SelectableChoiceCard';
import {
  CARESTART_DEEPGRAM_VOICES,
  CARESTART_OPENAI_VOICES,
} from './careStartTtsConstants';
import { GEMINI_LIVE_PREBUILT_VOICES } from 'services/voice/geminiLiveVoices';

/**
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   title: string,
 *   voiceSet: 'openai' | 'gemini' | 'deepgram',
 *   currentVoice: string,
 *   onSave: (voiceId: string) => void | Promise<void>,
 * }} props
 */
export default function CareStartTtsVoiceDialog({
  open,
  onClose,
  title,
  voiceSet,
  currentVoice,
  onSave,
}) {
  const [selected, setSelected] = useState(currentVoice);

  useEffect(() => {
    if (open) setSelected(currentVoice);
  }, [open, currentVoice]);

  const catalog =
    voiceSet === 'gemini'
      ? GEMINI_LIVE_PREBUILT_VOICES
      : voiceSet === 'deepgram'
        ? CARESTART_DEEPGRAM_VOICES
        : CARESTART_OPENAI_VOICES;
  const entries = Object.entries(catalog);

  const handleSave = async () => {
    await onSave(selected);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="carestart-tts-voice-dialog-title"
    >
      <DialogTitle id="carestart-tts-voice-dialog-title">{title}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose the voice used when CareStart speaks to the patient.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {entries.map(([id, info]) => (
            <SelectableChoiceCard
              key={id}
              title={id.charAt(0).toUpperCase() + id.slice(1)}
              description={`${info.gender} — ${info.description}`}
              selected={selected === id}
              onClick={() => setSelected(id)}
            />
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => void handleSave()}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

CareStartTtsVoiceDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  voiceSet: PropTypes.oneOf(['openai', 'gemini', 'deepgram']).isRequired,
  currentVoice: PropTypes.string.isRequired,
  onSave: PropTypes.func.isRequired,
};
